// Board tab membership and ordering. Adapted from EveryInc/tend src/feed/selectors.ts (MIT,
// Copyright (c) 2026 Every, Inc.): pass filter and tab membership kept; reading groups dropped;
// ordering made stable across refetches (ADHD principle 17: nothing reorders under the cursor).
import type { BoardView, BundleView, CardSummary, RoutineGroupView } from "@shared/api";
import type { RoundOrder } from "@shared/startEasily";
import { workNeedsDecision } from "./wording";

export type BoardTab = "review" | "progress" | "done";
export const BOARD_TABS: readonly BoardTab[] = ["review", "progress", "done"];
export const TAB_LABEL: Record<BoardTab, string> = {
  review: "To review",
  progress: "In progress",
  done: "Done",
};

export function isToReview(card: Pick<CardSummary, "status">): boolean {
  return card.status === "to_review_new" || card.status === "to_review_updated";
}

/**
 * A to-review card belongs to the current pass when it was made ready for it, or when it is an
 * updated member whose newer revision waits for the next pass (it keeps its place, showing the
 * presented revision). A new card made ready for a later pass waits ("Next round ready").
 * The server sends only pass members for review, so this matters for cards that are also listed
 * for other reasons (e.g. "Needs you").
 */
export function inCurrentPass(
  card: Pick<CardSummary, "readyForPass" | "status">,
  currentPass: number,
): boolean {
  return card.readyForPass <= currentPass || card.status === "to_review_updated";
}

/** Which tab shows a card; "next" means it waits for the next pass; null means not shown. */
export function cardTab(card: CardSummary, currentPass: number): BoardTab | "next" | null {
  if (card.routineGroupId) return null; // shown inside its routine group
  if (card.status === "blocked" || card.work?.status === "blocked") return "progress";
  switch (card.status) {
    case "to_review_new":
    case "to_review_updated":
      // U6: a pinned card stays in view across rounds, even if set aside or ready for the next.
      if (card.pinned) return "review";
      if (card.sweepHidden) return null;
      return inCurrentPass(card, currentPass) ? "review" : "next";
    case "queued":
    case "working":
      return "progress";
    case "done":
      // Made time for (U3): it waits under In progress until its focus block starts.
      return card.disposition === "snoozed" && card.focusBlock ? "progress" : "done";
  }
  return null;
}

export function routineTab(group: Pick<RoutineGroupView, "status">): BoardTab | null {
  switch (group.status) {
    case "proposed":
      return "review";
    case "approved":
    case "executing":
      return "progress";
    case "completed":
    case "partial":
      return "done";
    default:
      return null;
  }
}

/**
 * Server order, with sweep rank (when present) first. Stable: ties keep their server position. A
 * round started quick-first (U3) keeps the server's order exactly: it was chosen as the round
 * started and must not change under the cursor.
 */
export function reviewOrder(
  cards: readonly CardSummary[],
  order: RoundOrder = "standard",
): CardSummary[] {
  if (order === "quick_first") return [...cards];
  return cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const ra = a.card.sweepRank ?? Number.MAX_SAFE_INTEGER;
      const rb = b.card.sweepRank ?? Number.MAX_SAFE_INTEGER;
      return ra - rb || a.index - b.index;
    })
    .map((x) => x.card);
}

function progressRank(card: CardSummary): number {
  if (card.status === "blocked" || (card.work && workNeedsDecision(card.work))) return 0;
  return card.status === "working" ? 1 : 2;
}

/**
 * The In progress list in a fixed order: cards that need a decision first, then cards being
 * worked on, then queued ones (and cards waiting for a focus block). Ties keep their server
 * position, so a refetch never reshuffles rows under the cursor.
 */
export function progressOrder(cards: readonly CardSummary[]): CardSummary[] {
  return cards
    .map((card, index) => ({ card, index, rank: progressRank(card) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((x) => x.card);
}

function byCompletedDesc(a: CardSummary, b: CardSummary): number {
  return (b.completedAt ?? b.updatedAt).localeCompare(a.completedAt ?? a.updatedAt);
}

export interface BoardSections {
  review: CardSummary[];
  /** Queued, working and blocked cards, and cards waiting for a focus block (see progressOrder). */
  progress: CardSummary[];
  done: CardSummary[];
  /** Breakthrough cards still to review, in or out of this pass ("Needs you"). */
  breakthrough: CardSummary[];
  routine: Record<BoardTab, RoutineGroupView[]>;
  /**
   * Proposed bundles shown as one entry in To review (docs/design/bundles.md), with at least two
   * members in this round. `review` still holds every member: focus mode, the round's estimate
   * and its progress count cards; only the list (roundRows) folds members into their bundle.
   */
  bundles: BoardBundle[];
}

/**
 * A bundle as the board shows it: `cardIds` and `cards` are its members in this round's review
 * list, in bundle order (`drafted` and `total` stay as the server counted them).
 */
export interface BoardBundle extends BundleView {
  cards: CardSummary[];
}

/** A bundle needs two members in the round to be one entry; with fewer they are ordinary rows. */
export const BUNDLE_MIN_MEMBERS = 2;

/**
 * Proposed bundles with at least two members among `review`. A member is a review card listed
 * in the bundle's `cardIds` or carrying its `bundleId`; listed members come in bundle order,
 * others after them in review order. A card belongs to the first bundle that claims it.
 */
export function boardBundles(
  bundles: readonly BundleView[] | undefined,
  review: readonly CardSummary[],
): BoardBundle[] {
  if (!bundles?.length) return [];
  const byId = new Map(review.map((c) => [c.id, c] as const));
  const claimed = new Set<string>();
  const out: BoardBundle[] = [];
  for (const bundle of bundles) {
    if (bundle.status !== "proposed") continue;
    const listed = bundle.cardIds.flatMap((id) => byId.get(id) ?? []);
    const tagged = review.filter((c) => c.bundleId === bundle.id && !bundle.cardIds.includes(c.id));
    const cards = [...listed, ...tagged].filter((c) => !claimed.has(c.id));
    if (cards.length < BUNDLE_MIN_MEMBERS) continue;
    for (const c of cards) claimed.add(c.id);
    out.push({ ...bundle, cardIds: cards.map((c) => c.id), cards });
  }
  return out;
}

export function boardSections(
  board: Pick<BoardView, "cards" | "routineGroups" | "pass" | "bundles">,
): BoardSections {
  const current = board.pass.current;
  const sections: BoardSections = {
    review: [],
    progress: [],
    done: [],
    breakthrough: [],
    routine: { review: [], progress: [], done: [] },
    bundles: [],
  };
  for (const card of board.cards) {
    const tab = cardTab(card, current);
    if (tab && tab !== "next") sections[tab].push(card);
    if (card.breakthrough && isToReview(card) && !card.sweepHidden && !card.routineGroupId) {
      sections.breakthrough.push(card);
    }
  }
  sections.review = reviewOrder(sections.review, board.pass.order);
  sections.progress = progressOrder(sections.progress);
  sections.done.sort(byCompletedDesc);
  for (const group of board.routineGroups) {
    const tab = routineTab(group);
    if (tab) sections.routine[tab].push(group);
  }
  sections.bundles = boardBundles(board.bundles, sections.review);
  return sections;
}

/**
 * Keep the order the reader has already seen: known ids keep their relative position, ids that
 * left disappear, new ids are appended at the end (never inserted above the cursor).
 */
export function stableOrder(previous: readonly string[] | null, next: readonly string[]): string[] {
  if (!previous?.length) return [...next];
  const present = new Set(next);
  const kept = previous.filter((id) => present.has(id));
  const seen = new Set(kept);
  return [...kept, ...next.filter((id) => !seen.has(id))];
}

/** Progress through a pass: ids seen in it this session vs. ids still to review. */
export function passProgress(
  seen: readonly string[],
  stillToReview: readonly string[],
): { decided: number; total: number } {
  const open = new Set(stillToReview);
  const total = new Set([...seen, ...stillToReview]).size;
  const decided = [...new Set(seen)].filter((id) => !open.has(id)).length;
  return { decided, total };
}

/** The id to select after the list changed: the same id, else whatever took its place. */
export function reselect(
  ids: readonly string[],
  selected: string | null,
  lastIndex: number,
): string | null {
  if (!ids.length) return null;
  if (selected && ids.includes(selected)) return selected;
  const index = Math.min(Math.max(lastIndex, 0), ids.length - 1);
  return ids[index] ?? null;
}

/**
 * Entries in this round: cards plus one combined cleanup when any routine group is proposed (the
 * feed shows every proposed group as one "Routine cleanup" entry). Kept for the round bar's estimate and
 * its tooltip; never shown as a visible count.
 */
export function passCount(sections: BoardSections): number {
  return sections.review.length + (sections.routine.review.length ? 1 : 0);
}

/** How many "Needs you" rows lead the list; the rest stay in their place below. */
export const NEEDS_YOU_ROWS = 3;

/** One entry of the Waiting list: a card, or a bundle in the place of its first member. */
export type RoundEntry =
  { kind: "card"; card: CardSummary } | { kind: "bundle"; bundle: BoardBundle };

export interface RoundRows {
  /** At most NEEDS_YOU_ROWS breakthrough cards, first in the list (and in j/k). */
  needsYou: CardSummary[];
  /** Every breakthrough card waiting for review, in or out of this round. */
  needsYouTotal: number;
  /** Breakthrough cards not at the top that are still in the list below ("N more … below"). */
  moreBelow: number;
  /** This round's cards that are not at the top and not folded into a bundle, in order. */
  waiting: CardSummary[];
  /** The Waiting list in order: `waiting` with each bundle as one entry (one j/k stop). */
  entries: RoundEntry[];
}

/**
 * Split the review tab into "Needs you" (a few rows at the top) and "Waiting" (the rest).
 * Breakthrough cards made ready for a later round are picked first, since they have no place in
 * the list below; the rows keep their server order. Anything not picked stays where it was.
 *
 * Bundles: "Needs you" is picked exactly as without them, from every breakthrough card, bundle
 * members included; a picked member is an ordinary row at the top (and still listed inside its
 * bundle). In Waiting, a bundle is one entry in the place of its first member not at the top,
 * and its members are not listed on their own; a bundle whose members are all at the top has no
 * Waiting entry. A breakthrough member left inside a bundle counts toward "N more … below".
 */
export function roundRows(
  review: readonly CardSummary[],
  breakthrough: readonly CardSummary[],
  bundles: readonly BoardBundle[] = [],
  max = NEEDS_YOU_ROWS,
): RoundRows {
  const inRound = new Set(review.map((c) => c.id));
  const outside = breakthrough.filter((c) => !inRound.has(c.id));
  const inside = breakthrough.filter((c) => inRound.has(c.id));
  const picked = new Set([...outside, ...inside].slice(0, Math.max(0, max)).map((c) => c.id));
  const needsYou = breakthrough.filter((c) => picked.has(c.id));
  const bundleOf = new Map<string, BoardBundle>();
  for (const bundle of bundles) for (const id of bundle.cardIds) bundleOf.set(id, bundle);
  const placed = new Set<string>();
  const entries: RoundEntry[] = [];
  for (const card of review) {
    if (picked.has(card.id)) continue;
    const bundle = bundleOf.get(card.id);
    if (!bundle) entries.push({ kind: "card", card });
    else if (!placed.has(bundle.id)) {
      placed.add(bundle.id);
      entries.push({ kind: "bundle", bundle });
    }
  }
  return {
    needsYou,
    needsYouTotal: breakthrough.length,
    moreBelow: inside.filter((c) => !picked.has(c.id)).length,
    waiting: entries.flatMap((e) => (e.kind === "card" ? [e.card] : [])),
    entries,
  };
}

/** The j/k id of a Waiting entry. */
export function entryNavId(entry: RoundEntry): string {
  return entry.kind === "card" ? entry.card.id : bundleNavId(entry.bundle.id);
}

/** Past this many rows the Done tab renders in a window (only rows near the viewport). */
export const VIRTUALIZE_AFTER = 50;

export function shouldVirtualize(tab: BoardTab, rows: number): boolean {
  return tab === "done" && rows > VIRTUALIZE_AFTER;
}

const ROUTINE_PREFIX = "routine:";

/** List id for a routine group where groups and cards share one j/k list (board, focus). */
export function routineNavId(groupId: string): string {
  return `${ROUTINE_PREFIX}${groupId}`;
}

export function groupIdOfNavId(id: string | null | undefined): string | null {
  return id?.startsWith(ROUTINE_PREFIX) ? id.slice(ROUTINE_PREFIX.length) : null;
}

const BUNDLE_PREFIX = "bundle:";

/** List id for a bundle, which is one entry where bundles and cards share one j/k list. */
export function bundleNavId(bundleId: string): string {
  return `${BUNDLE_PREFIX}${bundleId}`;
}

export function bundleIdOfNavId(id: string | null | undefined): string | null {
  return id?.startsWith(BUNDLE_PREFIX) ? id.slice(BUNDLE_PREFIX.length) : null;
}
